import appConstants from "../../../../../appConstants";
import { v4 as uuidv4 } from "uuid";

const getDestinationRouteList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.map((stop) => {
      return { label: stop.routeName, value: stop.routeName, key: uuidv4() };
    })
  );
};

// get object and key value as param
// return a list of uniques value of that key
const getListByKey = (selectedStops, key) => {
  const uniqueList = [
    ...new Set(selectedStops.map((stop) => stop[key]).filter(Boolean)),
  ];

  const result = uniqueList.map((stopValue) => ({
    label: stopValue,
    value: stopValue,
    key: uuidv4(),
  }));

  return result;
};

const getCustomerInfoList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.reduce((result, stop) => {
      if (stop.consigneeName) {
        return [
          ...result,
          {
            label: stop.customerCode,
            value: stop.consigneeId,
            key: uuidv4(),
          },
        ];
      }
      return result;
    }, [])
  );
};

const getShipperInfoList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.reduce((result, stop) => {
      if (stop.shipperName) {
        return [
          ...result,
          {
            label: stop.shipperCode,
            value: stop.rmaxShipperCode,
            key: uuidv4(),
          },
        ];
      }
      return result;
    }, [])
  );
};

const getCustomerAddressList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.reduce((result, stop) => {
      if (stop.consigneeAddress) {
        return [
          ...result,
          {
            label: `${stop.consigneeAddress}, ${stop.city}, ${stop.state}, ${stop.zip}`,
            value: stop.consigneeId,
            key: uuidv4(),
          },
        ];
      }
      return result;
    }, [])
  );
};

const getShipperAddressList = (selectedStops) => {
  return getUniqueLabelValue(
    selectedStops.reduce((result, stop) => {
      if (stop.shipperAddress) {
        return [
          ...result,
          {
            label: `${stop.shipperAddress}, ${stop.shipperCity}, ${stop.shipperState}, ${stop.shipperZip}`,
            value: stop.rmaxShipperCode,
            key: uuidv4(),
          },
        ];
      }
      return result;
    }, [])
  );
};

const getPreferredRouteNameListForDeliveryStops = (customers, selectedStops) => {
  if (!customers) {
    return [];
  }
  const consigneeIdList = selectedStops.map(
    (customer) => customer.consigneeId
  );
  if (!consigneeIdList.length) {
    return [];
  }
  const routeSet = new Set();
  customers.forEach((customer) => {
    if (consigneeIdList.includes(customer.deliveryDetails?.customerId)) {
      if (customer.deliveryDetails.route) {
        routeSet.add({
          label: customer.deliveryDetails.route,
          value: customer.deliveryDetails.route,
          key: uuidv4(),
        });
      }
    }
  });
  return [...routeSet];
};

const getCustomersDeliveryWindowsDetail = (customers, stop) => {
  const customer = customers.find(
    (customer) => customer.deliveryDetails.customerId === stop.consigneeId
  );
  if (!customer) {
    return null;
  }
  let deliveryWindow = "";
  const {
    deliveryDetails: { receivingHours, openHours, closeHours },
  } = customer;
  if (receivingHours) {
    deliveryWindow = formateDeliveryWindowLabel(receivingHours);
  } else {
    deliveryWindow = formateDeliveryWindowLabel({ //this change is specific to ffe, same can be used in develop also
      "mon-sun": [`${openHours} - ${closeHours}`],
    });
  }
  return deliveryWindow && { label: deliveryWindow, value: stop.consigneeId };
};

const getShipperPickupWindowsDetail = (shippers, stop) => {
  const shipper = shippers.find(
    (shipper) => shipper.pickupDetails.rmaxShipperCode === stop.rmaxShipperCode
  );
  if (!shipper) {
    return null;
  }
  const {
    pickupDetails: { shippingHours, openHours, closeHours },
  } = shipper;
  let shipperWindow = "";
  if (shippingHours) {
    shipperWindow = formateDeliveryWindowLabel(shippingHours);
  } else {
    shipperWindow = formateDeliveryWindowLabel({
      "mon-sun": [`${openHours} - ${closeHours}`],
    });
  }
  return shipperWindow && { label: shipperWindow, value: stop.rmaxShipperCode }
};

// filter out duplicate label value from object list
const getUniqueLabelValue = (list) => {
  const filteredList = list.filter(Boolean);
  return filteredList.filter(
    (a, i) => filteredList.findIndex((s) => a.label === s.label) === i
  );
};

// formatting label into "dayName | time" => "Mon-Sun | 8:00 - 17:00"
const formateDeliveryWindowLabel = (data) => {
  const days = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    "mon-sun": "Mon-Sun",
  };
  return Object.entries(data)
    .map(([key, value]) => {
      return `${days[key]} | ${value}`;
    })
    .join(" \n");
};

const MERGE_TYPES = {
  Stops: "Stops",
  Customers: "Customers"
}

const isPickupStop = (stopType) => {
  return stopType === appConstants.ROUTE_STOP_TYPES.PU;
}

const isDeliveryStop = (stopType) => {
  return !isPickupStop(stopType);
}

const getCustomerName = ({ stopType, shipperName, consigneeName }) => {
  return isPickupStop(stopType) ? shipperName : consigneeName;
}

const getStopCustomerInfo = (stopData) => {
  if (isPickupStop(stopData.stopType)) {
    return {
      name: stopData.shipperName,
      address: `${stopData.shipperAddress}, ${stopData.shipperCity}`,
    };
  }
  return {
    name: stopData.consigneeName,
    address: `${stopData.consigneeAddress}, ${stopData.city}`,
  };
};

const getDefaultMergeDeliveryStopInfo = () => {
  return {
    mergedCustomerId: null,
    customerName: null,
    customerPreferredRouteName: null,
    customerIdForAddress: null,
    customerIdForDeliveryWindow: null,
    duplicateCustomerId: null,
    destinationRouteName: null,
  };
};

const getDefaultMergePickupStopInfo = () => {
  return {
    mergedRmaxShipperCode: null,
    shipperName: null,
    rmaxShipperCodeForAddress: null,
    rmaxShipperCodeForPickupWindow: null,
    duplicateRmaxShipperCode: null,
    destinationRouteName: null,
  };
};

const getCustomerFieldError = ({
  field,
  value,
  customerPreferredRouteNameList,
  deliveryWindowList,
  destinationRouteList,
}) => {
  switch (field) {
    case "mergedCustomerId":
      return value ? null : "* Customer is required";
    case "customerName":
      return value ? null : "* Customer name is required";
    case "customerPreferredRouteName":
      return customerPreferredRouteNameList.length > 0
        ? value
          ? null
          : "* Customer Preferred RouteName is required"
        : null;
    case "customerIdForAddress":
      return value ? null : "* Address is required";
    case "customerIdForDeliveryWindow":
      return deliveryWindowList.length > 0
        ? value
          ? null
          : "* Delivery Window is required"
        : null;
    case "destinationRouteName":
      return destinationRouteList.length > 1
        ? value
          ? null
          : "* Destination RouteName is required"
        : null;
    default:
      return null;
  }
};

const getShipperFieldError = ({
  field,
  value,
  pickupWindowList,
  destinationRouteList,
}) => {
  switch (field) {
    case "mergedRmaxShipperCode":
      return value ? null : "* Shipper is required";
    case "shipperName":
      return value ? null : "* Shipper name is required";
    case "rmaxShipperCodeForAddress":
      return value ? null : "* Address is required";
    case "rmaxShipperCodeForPickupWindow":
      return pickupWindowList.length > 0
        ? value
          ? null
          : "* Delivery Window is required"
        : null;
    case "destinationRouteName":
      return destinationRouteList.length > 1
        ? value
          ? null
          : "* Destination RouteName is required"
        : null;
    default:
      return null;
  }
};

export {
  getUniqueLabelValue,
  getCustomerAddressList,
  getShipperAddressList,
  getCustomerInfoList,
  getShipperInfoList,
  getDestinationRouteList,
  getListByKey,
  MERGE_TYPES,
  isPickupStop,
  isDeliveryStop,
  getCustomerName,
  getStopCustomerInfo,
  getPreferredRouteNameListForDeliveryStops,
  getCustomersDeliveryWindowsDetail,
  getShipperPickupWindowsDetail,
  getCustomerFieldError,
  getShipperFieldError,
  getDefaultMergeDeliveryStopInfo,
  getDefaultMergePickupStopInfo
};
